import styled from 'styled-components';

import Info from 'assets/icons/status/icon-info-16.svg';

import Button from 'basics/buttons/Button';
import Select from 'basics/inputs/Select';
import ToggleGroup from 'basics/inputs/ToggleGroup';
import { ExternalLink } from 'basics/links';

import {
    cardBoxShadow,
    flexAllCenter,
    flexRowSpaceBetween,
    respondDown,
    textEllipsis,
} from 'styles/mixins';
import { Breakpoints, COLORS, FONT_SIZE } from 'styles/style-constants';

export const PoolsListBlock = styled.div<{ $onlyList: boolean }>`
    display: flex;
    flex-direction: column;
    background-color: ${COLORS.white};
    margin: 4.8rem auto 5rem;

    ${({ $onlyList }) =>
        !$onlyList &&
        `
        padding: 4.8rem;
        border-radius: 0.5rem;
        margin-top: 0;
        ${cardBoxShadow};
    `}

    ${respondDown(Breakpoints.md)`
        padding: 3.2rem 0;
        background-color: ${COLORS.gray50};
    `}
`;

export const ListHeader = styled.div`
    ${flexRowSpaceBetween};
    margin-bottom: 2rem;

    ${respondDown(Breakpoints.sm)`
        flex-direction: column;
    `}
`;

export const ListTitle = styled.h3`
    font-size: 3.6rem;
    line-height: 4.2rem;
    font-weight: 400;

    ${respondDown(Breakpoints.sm)`
        font-size: 2.8rem;
    `}
`;

export const ListTotal = styled.span`
    font-size: 1.6rem;
    line-height: 2.8rem;

    span:last-child {
        font-weight: 700;
    }
`;

export const Section = styled.div`
    flex: 1 0 auto;
    ${flexAllCenter};
`;

export const LoginButton = styled(Button)`
    margin-top: 1rem;
`;

export const ToggleGroupStyled = styled(ToggleGroup)`
    width: fit-content;
    margin-bottom: 4rem;

    ${respondDown(Breakpoints.md)`
        display: none;
    `}
`;

export const SelectWrapper = styled.div`
    display: none;

    ${respondDown(Breakpoints.md)`
        display: flex;
        padding: 0 1.6rem;
    `}
`;

export const SelectStyled = styled(Select)`
    margin-bottom: 4rem;

    ${respondDown(Breakpoints.md)`
        display: flex;
    `}
`;

export const Buttons = styled.div`
    display: flex;
    gap: 0.8rem;
`;

export const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;

    ${respondDown(Breakpoints.xl)`
        width: 100%;
        flex-direction: column;
        align-items: stretch;

        ${Buttons} {
            justify-content: flex-end;
        }
    `}
`;

export const NoRowToggle = styled.div`
    display: inline-flex;
    align-items: center;
    min-width: 0;
`;

export const Pooled = styled.div`
    display: flex;
    gap: 0.8rem;
    align-items: center;
`;

export const TooltipInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    min-width: 24rem;
    max-width: calc(100vw - 12rem);
`;

export const TooltipRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 1.4rem;
    line-height: 2rem;
    gap: 2rem;

    span:first-child {
        color: ${COLORS.textGray};
        width: 60%;
        ${textEllipsis};
    }

    span:last-child {
        color: ${COLORS.textTertiary};
        display: flex;
        gap: 0.5rem;
        align-items: center;
    }
`;

export const IconInfoStyled = styled(Info)`
    cursor: help;
`;

export const LabelInner = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 400;
    ${FONT_SIZE.sm};
`;

export const PairWithChevron = styled.div`
    display: grid;
    grid-template-columns: 3.2rem minmax(0, 1fr);
    align-items: center;
    width: 100%;
    min-width: 0;

    ${respondDown(Breakpoints.xl)`
        grid-template-columns: minmax(0, 1fr);
    `}
`;

export const ChevronSlot = styled.div`
    width: 3.2rem;
    height: 3.2rem;
    flex: 0 0 auto;

    ${respondDown(Breakpoints.xl)`
        display: none;
    `}
`;

export const ChevronButton = styled.button<{ $expanded: boolean }>`
    ${flexAllCenter};
    width: 3.2rem;
    height: 3.2rem;
    padding: 0;
    border: none;
    border-radius: 0.4rem;
    background: ${COLORS.transparent};
    color: ${COLORS.textPrimary};
    cursor: pointer;
    transition:
        background 0.15s ease,
        transform 0.2s ease;

    svg {
        width: 1.6rem;
        height: 1.6rem;
        transition: transform 0.2s ease;
        transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
    }

    &:hover {
        background: ${COLORS.gray100};
    }
`;

export const MobileChevronButton = styled(ChevronButton)`
    display: none;

    ${respondDown(Breakpoints.xl)`
        display: flex;
        width: 100%;
        background: ${COLORS.gray50};

        &:hover {
            background: ${COLORS.gray100};
        }
    `}
`;

export const AfterRowWrapper = styled.div`
    width: 100%;
    padding: 0 0 1.6rem;

    ${respondDown(Breakpoints.xl)`
        padding: 0;
    `}
`;

export const ListContent = styled.div`
    min-height: 32rem;
`;

export const EmptyState = styled.div`
    ${flexAllCenter};
    flex-direction: column;
    min-height: 32rem;
    padding: 3.2rem 1.6rem;
    text-align: center;

    h3 {
        margin-bottom: 0.9rem;
        color: ${COLORS.textPrimary};
        font-size: 2rem;
        line-height: 2.8rem;
    }
    span {
        color: ${COLORS.textGray};
        line-height: 180%;
    }
    a {
        color: ${COLORS.purple500};
    }
`;

export const PoolIdentityLoading = styled.div`
    display: flex;
    align-items: center;
    gap: 1.6rem;
    padding-left: 3.2rem;
    min-height: 9.6rem;
    ${respondDown(Breakpoints.xl)`padding-left: 0;`}
    ${respondDown(Breakpoints.md)`
        flex-direction: column;
        align-items: flex-start;
        padding-top: 1.6rem;
        min-height: 13.2rem;
    `}
`;

export const BrowsePoolsLink = styled(ExternalLink)`
    font-size: 1.4rem;
    line-height: 2rem;
`;
